import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  Notification,
  NOTIFICATION_PRIORITY,
  NOTIFICATION_TYPE,
  Student,
  Teacher,
  User,
} from "../models";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

class NotificationController {
  /**
   * Get all notifications for the authenticated user
   * @route GET /api/v1/notifications
   * @access Private
   */
  async getNotifications(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { isRead, read, type, priority, page = 1, limit = 20 } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const filterQuery: any = { userId: new mongoose.Types.ObjectId(userId) };

      const readFilter = read ?? isRead;
      if (readFilter !== undefined) {
        filterQuery.isRead = readFilter === "true";
      }

      if (type) {
        filterQuery.type = type;
      }

      if (priority) {
        filterQuery.priority = priority;
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const notifications = await Notification.find({ ...filterQuery })
        .sort({ createdAt: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await Notification.countDocuments(filterQuery);
      const unreadCount = await Notification.countDocuments({
        userId: new mongoose.Types.ObjectId(userId),
        isRead: false,
      });

      res.status(200).json({
        success: true,
        message: "Notifications retrieved successfully",
        data: {
          notifications: notifications.map(notif => ({
            id: notif.id,
            type: notif.type,
            title: notif.title,
            body: notif.message,
            message: notif.message,
            read: notif.isRead,
            isRead: notif.isRead,
            priority: notif.priority,
            readAt: notif.readAt,
            actionUrl: notif.actionUrl,
            data: notif.metadata,
            metadata: notif.metadata,
            createdAt: notif.createdAt,
          })),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
          unreadCount,
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get notifications error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve notifications",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get unread notifications count
   * @route GET /api/v1/notifications/unread-count
   * @access Private
   */
  async getUnreadCount(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const unreadCount = await Notification.countDocuments({
        userId: new mongoose.Types.ObjectId(userId),
        isRead: false,
      });

      res.status(200).json({
        success: true,
        message: "Unread count retrieved successfully",
        data: {
          unreadCount,
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get unread count error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve unread count",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Mark notification as read
   * @route PUT /api/v1/notifications/:id/read
   * @access Private
   */
  async markAsRead(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid notification ID",
        } as APIResponse);
        return;
      }

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const notification = await Notification.findOne({
        _id: id,
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!notification) {
        res.status(404).json({
          success: false,
          message: "Notification not found",
        } as APIResponse);
        return;
      }

      await notification.markAsRead();

      logger.info(`Notification marked as read: ${notification.id}`, {
        notificationId: notification.id,
        userId,
      });

      res.status(200).json({
        success: true,
        message: "Notification marked as read",
        data: {
          notification: {
            id: notification.id,
            isRead: notification.isRead,
            readAt: notification.readAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Mark notification as read error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to mark notification as read",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Mark all notifications as read
   * @route PUT /api/v1/notifications/read-all
   * @access Private
   */
  async markAllAsRead(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const result = await Notification.updateMany(
        {
          userId: new mongoose.Types.ObjectId(userId),
          isRead: false,
        },
        {
          $set: {
            isRead: true,
            readAt: new Date(),
          },
        },
      );

      logger.info(`All notifications marked as read for user: ${userId}`, {
        userId,
        updatedCount: result.modifiedCount,
      });

      res.status(200).json({
        success: true,
        message: "All notifications marked as read",
        data: {
          updatedCount: result.modifiedCount,
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Mark all notifications as read error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to mark all notifications as read",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Delete a notification
   * @route DELETE /api/v1/notifications/:id
   * @access Private
   */
  async deleteNotification(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid notification ID",
        } as APIResponse);
        return;
      }

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const notification = await Notification.findOneAndDelete({
        _id: id,
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!notification) {
        res.status(404).json({
          success: false,
          message: "Notification not found",
        } as APIResponse);
        return;
      }

      logger.info(`Notification deleted: ${notification.id}`, {
        notificationId: notification.id,
        userId,
      });

      res.status(200).json({
        success: true,
        message: "Notification deleted successfully",
      } as APIResponse);
    } catch (error: any) {
      logger.error("Delete notification error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to delete notification",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Create a notification (Internal use or admin)
   * @route POST /api/v1/notifications
   * @access Private (Admin or System)
   */
  async createNotification(req: AuthRequest, res: Response): Promise<void> {
    try {
      const {
        userId,
        type,
        title,
        message,
        priority,
        actionUrl,
        metadata,
        schoolId,
      } = req.body;
      const requesterRole = req.user?.role;

      // Only allow admin or system to create notifications
      if (requesterRole !== "admin") {
        res.status(403).json({
          success: false,
          message: "Only admins can create notifications",
        } as APIResponse);
        return;
      }

      if (!userId || !type || !title || !message) {
        res.status(400).json({
          success: false,
          message: "Missing required fields: userId, type, title, message",
        } as APIResponse);
        return;
      }

      const notification = new Notification({
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        userId: new mongoose.Types.ObjectId(userId),
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        schoolId: schoolId ? new mongoose.Types.ObjectId(schoolId) : undefined,
        type,
        title,
        message,
        priority: priority || NOTIFICATION_PRIORITY.MEDIUM,
        actionUrl,
        metadata: metadata || {},
        isRead: false,
      });

      await notification.save();

      logger.info(`Notification created: ${notification.id}`, {
        notificationId: notification.id,
        userId,
        type,
      });

      res.status(201).json({
        success: true,
        message: "Notification created successfully",
        data: {
          notification: {
            id: notification.id,
            type: notification.type,
            title: notification.title,
            message: notification.message,
            priority: notification.priority,
            createdAt: notification.createdAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Create notification error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to create notification",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get notifications for student (with role-specific filtering)
   * @route GET /api/v1/student/notifications
   * @access Private (Student)
   */
  async getStudentNotifications(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { isRead, type, priority, page = 1, limit = 20 } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      // Verify student role
      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!student) {
        res.status(403).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      const filterQuery: any = { userId: new mongoose.Types.ObjectId(userId) };

      if (isRead !== undefined) {
        filterQuery.isRead = isRead === "true";
      }

      if (type) {
        filterQuery.type = type;
      }

      if (priority) {
        filterQuery.priority = priority;
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      const notifications = await Notification.find({ ...filterQuery })
        .sort({ createdAt: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await Notification.countDocuments(filterQuery);
      const unreadCount = await Notification.countDocuments({
        userId: new mongoose.Types.ObjectId(userId),
        isRead: false,
      });

      // Format with relative time
      const formattedNotifications = notifications.map(notif => {
        const createdAt = new Date(notif.createdAt);
        const now = new Date();
        const diffMs = now.getTime() - createdAt.getTime();
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

        let timeAgo = "";
        if (diffMins < 1) {
          timeAgo = "just now";
        } else if (diffMins < 60) {
          timeAgo = `${diffMins} ${diffMins === 1 ? "min" : "mins"} ago`;
        } else if (diffHours < 24) {
          timeAgo = `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
        } else {
          timeAgo = createdAt.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        }

        return {
          id: notif.id,
          type: notif.type,
          title: notif.title,
          message: notif.message,
          priority: notif.priority,
          isRead: notif.isRead,
          readAt: notif.readAt,
          actionUrl: notif.actionUrl,
          metadata: notif.metadata,
          createdAt: notif.createdAt,
          timeAgo,
        };
      });

      res.status(200).json({
        success: true,
        message: "Notifications retrieved successfully",
        data: {
          notifications: formattedNotifications,
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
          unreadCount,
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Get student notifications error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve notifications",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get notifications for teacher
   * @route GET /api/v1/teacher/notifications
   * @access Private (Teacher)
   */
  async getTeacherNotifications(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { isRead, type, priority, page = 1, limit = 20 } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      // Verify teacher role
      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!teacher) {
        res.status(403).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      const filterQuery: any = { userId: new mongoose.Types.ObjectId(userId) };

      if (isRead !== undefined) {
        filterQuery.isRead = isRead === "true";
      }

      if (type) {
        filterQuery.type = type;
      }

      if (priority) {
        filterQuery.priority = priority;
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      const notifications = await Notification.find({ ...filterQuery })
        .sort({ createdAt: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await Notification.countDocuments(filterQuery);
      const unreadCount = await Notification.countDocuments({
        userId: new mongoose.Types.ObjectId(userId),
        isRead: false,
      });

      // Format with relative time
      const formattedNotifications = notifications.map(notif => {
        const createdAt = new Date(notif.createdAt);
        const now = new Date();
        const diffMs = now.getTime() - createdAt.getTime();
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

        let timeAgo = "";
        if (diffMins < 1) {
          timeAgo = "just now";
        } else if (diffMins < 60) {
          timeAgo = `${diffMins} ${diffMins === 1 ? "min" : "mins"} ago`;
        } else if (diffHours < 24) {
          timeAgo = `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
        } else {
          timeAgo = createdAt.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        }

        return {
          id: notif.id,
          type: notif.type,
          title: notif.title,
          message: notif.message,
          priority: notif.priority,
          isRead: notif.isRead,
          readAt: notif.readAt,
          actionUrl: notif.actionUrl,
          metadata: notif.metadata,
          createdAt: notif.createdAt,
          timeAgo,
        };
      });

      res.status(200).json({
        success: true,
        message: "Notifications retrieved successfully",
        data: {
          notifications: formattedNotifications,
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
          unreadCount,
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Get teacher notifications error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve notifications",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get notifications for school admin
   * @route GET /api/v1/school/notifications
   * @access Private (School Admin)
   */
  async getSchoolNotifications(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const { isRead, type, priority, page = 1, limit = 20 } = req.query;

      if (!userId || !schoolId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        userId: new mongoose.Types.ObjectId(userId),
        schoolId: new mongoose.Types.ObjectId(schoolId),
      };

      if (isRead !== undefined) {
        filterQuery.isRead = isRead === "true";
      }

      if (type) {
        filterQuery.type = type;
      }

      if (priority) {
        filterQuery.priority = priority;
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      const notifications = await Notification.find({ ...filterQuery })
        .sort({ createdAt: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await Notification.countDocuments(filterQuery);
      const unreadCount = await Notification.countDocuments({
        userId: new mongoose.Types.ObjectId(userId),
        schoolId: new mongoose.Types.ObjectId(schoolId),
        isRead: false,
      });

      // Format with relative time
      const formattedNotifications = notifications.map(notif => {
        const createdAt = new Date(notif.createdAt);
        const now = new Date();
        const diffMs = now.getTime() - createdAt.getTime();
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

        let timeAgo = "";
        if (diffMins < 1) {
          timeAgo = "just now";
        } else if (diffMins < 60) {
          timeAgo = `${diffMins} ${diffMins === 1 ? "min" : "mins"} ago`;
        } else if (diffHours < 24) {
          timeAgo = `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
        } else {
          timeAgo = createdAt.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        }

        return {
          id: notif.id,
          type: notif.type,
          title: notif.title,
          message: notif.message,
          priority: notif.priority,
          isRead: notif.isRead,
          readAt: notif.readAt,
          actionUrl: notif.actionUrl,
          metadata: notif.metadata,
          createdAt: notif.createdAt,
          timeAgo,
        };
      });

      res.status(200).json({
        success: true,
        message: "Notifications retrieved successfully",
        data: {
          notifications: formattedNotifications,
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
          unreadCount,
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Get school notifications error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve notifications",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get notifications for admin
   * @route GET /api/v1/admin/notifications
   * @access Private (Admin)
   */
  async getAdminNotifications(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const {
        isRead,
        type,
        priority,
        schoolId,
        page = 1,
        limit = 20,
      } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const filterQuery: any = { userId: new mongoose.Types.ObjectId(userId) };

      if (isRead !== undefined) {
        filterQuery.isRead = isRead === "true";
      }

      if (type) {
        filterQuery.type = type;
      }

      if (priority) {
        filterQuery.priority = priority;
      }

      if (schoolId) {
        filterQuery.schoolId = new mongoose.Types.ObjectId(schoolId as string);
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      const notifications = await Notification.find({ ...filterQuery })
        .sort({ createdAt: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await Notification.countDocuments(filterQuery);
      const unreadCount = await Notification.countDocuments({
        userId: new mongoose.Types.ObjectId(userId),
        isRead: false,
      });

      // Format with relative time
      const formattedNotifications = notifications.map(notif => {
        const createdAt = new Date(notif.createdAt);
        const now = new Date();
        const diffMs = now.getTime() - createdAt.getTime();
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

        let timeAgo = "";
        if (diffMins < 1) {
          timeAgo = "just now";
        } else if (diffMins < 60) {
          timeAgo = `${diffMins} ${diffMins === 1 ? "min" : "mins"} ago`;
        } else if (diffHours < 24) {
          timeAgo = `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
        } else {
          timeAgo = createdAt.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        }

        return {
          id: notif.id,
          type: notif.type,
          title: notif.title,
          message: notif.message,
          priority: notif.priority,
          isRead: notif.isRead,
          readAt: notif.readAt,
          actionUrl: notif.actionUrl,
          metadata: notif.metadata,
          createdAt: notif.createdAt,
          timeAgo,
        };
      });

      res.status(200).json({
        success: true,
        message: "Notifications retrieved successfully",
        data: {
          notifications: formattedNotifications,
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
          unreadCount,
        },
      } as APIResponse);
    } catch (error: unknown) {
      logger.error("Get admin notifications error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve notifications",
        error: error instanceof Error ? error.message : "Internal server error",
      } as APIResponse);
    }
  }
}

export const notificationController = new NotificationController();
